import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit }                    from '@angular/core';
import { LoginConfigService }                                                        from './login-config.service';
import { LoginQuery }                                                                from './state/login.query';
import { LoginService }                                                              from './state/login.service';
import { Observable, merge, of, Subscription }                                       from 'rxjs';
import { Router, RouterOutlet, NavigationEnd }                                       from '@angular/router';
import { animate, animateChild, query, sequence, style, transition, trigger }        from '@angular/animations';
import { filter, map }                                                               from 'rxjs/operators';


@Component({
	selector   : 'pmc-login',
	templateUrl: './login.component.html',
	standalone : false,
	animations : [
		trigger('fadeAnimation', [

			transition('* => *', [
				query(':enter, :leave', style({
					display : 'block',
					width   : 300
				}),
				{optional: true}),
				query(':enter', style({opacity: 0}),
				{optional: true}),
				sequence([
					query(':leave', animateChild(),
						{optional: true}),
					// Old content fades out first
					query(':leave', [
						style({opacity: 1}),
						animate('0.25s ease-out',
							style({opacity: 0}))
					],
					{optional: true}),
					// New content waits for card resize + header to settle, then fades in
					query(':enter', [
						style({opacity: 0}),
						animate('0.4s 0.8s ease-out',
							style({opacity: 1}))
					],
					{optional: true}),
					query(':enter', animateChild(),
						{optional: true})
				])
			])
		]),
		trigger(
			'textAnimation',
			[
				transition(
					'* => *',
					[
						style({transform: 'translateY(-100%)', opacity: 0}),
						animate('300ms', style({transform: 'translateY(0)', opacity: 1}))
					]
				)
			]
		)]
})

export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {

	$underTitleMessage: Observable<string>;
	$loginMessage: Observable<string>;
	$isErrorState: Observable<boolean>;
	$isStepTwo: Observable<boolean>;
	$hideLogo: Observable<boolean>;
	$hideHeader: Observable<boolean>;

	@ViewChild('o', {static: false}) outlet?: RouterOutlet;
	fadeAnimationState: any = '';
	private routerSubscription?: Subscription;
	private loginResetSubscription?: Subscription;

	constructor(public loginConfig: LoginConfigService,
												private loginService: LoginService,
												private loginQuery: LoginQuery,
												private router: Router) {
		// Reset all transient UI state on every (re)load so stale persisted
		// values (e.g. hideHeader: true from the wizard) never leak onto the
		// login form after a page refresh.
		this.loginService.setTitleMessage(this.loginConfig.loginWelcomeMessage);
		this.loginService.setHideHeader(false);
		this.loginService.setHideLogo(false);
		this.loginService.setUnderTitleMessage('');

		this.$isErrorState      = loginQuery.select(store => store.inErrorState);
		this.$loginMessage      = loginQuery.select(store => store.titleMessage);
		this.$underTitleMessage = loginQuery.select(store => store.underTitleMessage);
		this.$hideLogo          = loginQuery.select(store => store.hideLogo);
		this.$hideHeader        = loginQuery.select(store => store.hideHeader);

		// Track if we're on the request-access step-two route (email-token or LDAP flow)
		const isStepTwoUrl = (url: string) =>
			url.includes('/request-access/verify') || url.includes('/request-access/ldap') || url.includes('/request-access/sso');

		this.$isStepTwo = merge(
			of(isStepTwoUrl(this.router.url)),
			this.router.events.pipe(
				filter(event => event instanceof NavigationEnd),
				map(() => isStepTwoUrl(this.router.url))
			)
		);

		// Reset header state whenever the user navigates back to the base login
		// route (away from any request-access sub-route).  This covers the
		// in-session case where LoginComponent is never destroyed between
		// navigations, so the constructor reset above cannot fire again.
		this.loginResetSubscription = this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			filter(() => !this.router.url.includes('/request-access'))
		).subscribe(() => {
			this.loginService.setHideHeader(false);
			this.loginService.setHideLogo(false);
			this.loginService.setTitleMessage(this.loginConfig.loginWelcomeMessage);
			this.loginService.setUnderTitleMessage('');
		});
	}

	ngOnInit() {
		// Initialize animation state before first check
		this.fadeAnimationState = '';
	}

	ngAfterViewInit() {
		// Update animation state after view initialization
		// Check multiple times to catch when outlet becomes available
		setTimeout(() => {
			this.updateAnimationState();
			// Check again after a short delay to ensure outlet is ready
			setTimeout(() => {
				this.updateAnimationState();
			}, 100);
		}, 0);

		// Subscribe to router events to update animation state after navigation
		this.routerSubscription = this.router.events.pipe(
																																filter(event => event instanceof NavigationEnd)
																															)
																															.subscribe(() => {
																																// Use setTimeout to update after change detection cycle
																																setTimeout(() => {
																																	this.updateAnimationState();
																																}, 0);
																															});
	}

	ngOnDestroy() {
		if (this.routerSubscription) {
			this.routerSubscription.unsubscribe();
		}
		if (this.loginResetSubscription) {
			this.loginResetSubscription.unsubscribe();
		}
	}

	private updateAnimationState() {
		if (this.outlet) {
			// Use the route object directly for animation
			// This ensures the animation trigger fires correctly
			const newState = this.outlet.isActivated
																			? this.outlet.activatedRoute
																			: '';
			// Only update if the state actually changed to avoid unnecessary change detection
			if (this.fadeAnimationState !== newState) {
				this.fadeAnimationState = newState;
			}
		} else {
			// If outlet not available yet, keep current state or set to empty string
			if (this.fadeAnimationState !== '') {
				this.fadeAnimationState = '';
			}
		}
	}

	public getRouterOutletState(outlet: RouterOutlet) {
		// Return the current animation state property
		// This avoids calling outlet.isActivated during change detection
		return this.fadeAnimationState;
	}

	navigateToWebsite() {
		window.open('https://cohelion.com', '_blank');
	}
}
